import { useEffect, useMemo, useRef, useState } from 'react';
import { marked } from 'marked';
import hljs from 'highlight.js';
import 'highlight.js/styles/github-dark.css';
import { parseFrontmatter } from '../../utils/frontmatter';

interface MarkdownPreviewProps {
  content: string;
}

const FRONTMATTER_LABELS: Record<string, string> = {
  title: 'Title',
  slug: 'Slug',
  description: 'Description',
  date: 'Date',
  author: 'Author',
  category: 'Category',
  status: 'Status',
  tags: 'Tags',
  layout: 'Layout',
  permalink: 'Permalink',
  featured_image: 'Image',
  excerpt: 'Excerpt',
};

function FrontmatterBar({ frontmatter, wordCount }: { frontmatter: Record<string, string | undefined>; wordCount: number }) {
  const entries = Object.entries(frontmatter).filter(([, v]) => v);
  if (entries.length === 0 && wordCount === 0) return null;

  const readTime = Math.max(1, Math.round(wordCount / 238));

  return (
    <div className="mb-6 rounded-xl border border-slate-200 bg-slate-50/80 overflow-hidden">
      <div className="flex items-center gap-3 px-4 py-2.5 border-b border-slate-200/60 bg-white/60">
        <svg className="w-4 h-4 text-slate-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
        </svg>
        <span className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Document Info</span>
        <div className="ml-auto flex items-center gap-3 text-[11px] text-slate-500">
          <span>{wordCount.toLocaleString()} words</span>
          <span className="text-slate-300">·</span>
          <span>{readTime} min read</span>
        </div>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 divide-y sm:divide-y-0 sm:divide-x divide-slate-200/60">
        {entries.map(([key, value]) => (
          <div key={key} className="px-4 py-2.5 flex items-start gap-2">
            <span className="text-[11px] font-semibold text-slate-400 uppercase tracking-wider shrink-0 mt-0.5 w-20">
              {FRONTMATTER_LABELS[key] || key}
            </span>
            <span className="text-sm text-slate-700 break-words min-w-0">
              {(value || '').length > 120 ? value!.slice(0, 120) + '...' : value}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}

export function MarkdownPreview({ content }: MarkdownPreviewProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [copiedBlock, setCopiedBlock] = useState<number | null>(null);
  const parsed = useMemo(() => parseFrontmatter(content), [content]);

  useEffect(() => {
    // Configure marked with custom renderer
    const renderer = new marked.Renderer();

    // Custom link renderer - open external links in new tab
    renderer.link = (href, title, text) => {
      const isExternal = href?.startsWith('http');
      return `<a href="${href}" ${title ? `title="${title}"` : ''} ${isExternal ? 'target="_blank" rel="noopener noreferrer"' : ''}>${text}</a>`;
    };

    // Custom checkbox for task lists
    renderer.listitem = (text, task, checked) => {
      if (task) {
        return `<li class="task-list-item"><input type="checkbox" ${checked ? 'checked' : ''} disabled />${text}</li>`;
      }
      return `<li>${text}</li>`;
    };

    marked.setOptions({
      renderer,
      gfm: true,
      breaks: true,
    });

    // Apply syntax highlighting and add copy buttons
    if (containerRef.current) {
      containerRef.current.querySelectorAll('pre code').forEach((block, index) => {
        hljs.highlightElement(block as HTMLElement);

        // Add copy button
        const pre = block.parentElement;
        if (pre && !pre.querySelector('.copy-btn')) {
          const copyBtn = document.createElement('button');
          copyBtn.className = 'copy-btn';
          copyBtn.innerHTML = `
            <svg class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
            </svg>
          `;
          copyBtn.onclick = async () => {
            await navigator.clipboard.writeText(block.textContent || '');
            setCopiedBlock(index);
            setTimeout(() => setCopiedBlock(null), 2000);
          };
          pre.style.position = 'relative';
          pre.appendChild(copyBtn);
        }
      });
    }
  }, [parsed.body]);

  // Update copy button states
  useEffect(() => {
    if (containerRef.current) {
      containerRef.current.querySelectorAll('.copy-btn').forEach((btn, index) => {
        if (copiedBlock === index) {
          btn.innerHTML = `
            <svg class="w-4 h-4 text-green-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7" />
            </svg>
          `;
        }
      });
    }
  }, [copiedBlock]);

  const html = marked(parsed.body) as string;
  const hasFrontmatter = Object.keys(parsed.frontmatter).length > 0;

  return (
    <div>
      {hasFrontmatter && (
        <FrontmatterBar frontmatter={parsed.frontmatter} wordCount={parsed.wordCount} />
      )}
      {!hasFrontmatter && parsed.wordCount > 0 && (
        <div className="mb-4 flex items-center gap-3 text-[11px] text-slate-400">
          <span>{parsed.wordCount.toLocaleString()} words</span>
          <span>·</span>
          <span>{Math.max(1, Math.round(parsed.wordCount / 238))} min read</span>
        </div>
      )}
      <div
        ref={containerRef}
        className="markdown-preview prose-custom"
        dangerouslySetInnerHTML={{ __html: html }}
      />
    </div>
  );
}
